import moment from 'moment';
import PropTypes from 'prop-types';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import { Box, FormHelperText, InputAdornment } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

import { useMuiPickerLocale } from 'shared/lib/i18n';

import { getMomentDateAndTime } from '../Amenity/utils';

import StyledTextField from '../Shared/StyledTextField';
import StyledTimePicker from '../TimePicker';

const DateTimePicker = (props) => {
  const { localeText } = useMuiPickerLocale();
  const {
    input: { value, ...inputProps },
    meta: { error, touched },
    disabled,
    disablePast,
    disableFuture,
    disabledTime,
    onChangeFormatter,
    shouldDisableDate,
    getDisabledHours,
    getDisabledMinutes,
    showSecond = false,
    minuteStep = 15,
    format = 'hh:mm A',
    use12Hours = true,
    input: { propsInput },
    showTimePicker = true,
    style,
    setReservationDate,
  } = props;

  const onChange = (date) => {
    setReservationDate(getMomentDateAndTime(onChangeFormatter(date, value)));
    Date.parse(date)
      ? inputProps.onChange(onChangeFormatter(date, value))
      : inputProps.onChange(null);
  };

  const hasError = touched && !!error;
  const fieldName = propsInput?.name;
  const isTimePickerDisabled = disabled || disabledTime;

  const textField = (params) => (
    <StyledTextField
      {...params}
      error={!!error && touched}
      helperText=""
      style={style}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <EventNoteOutlinedIcon />
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} localeText={localeText}>
      <Box display="flex">
        <MobileDatePicker
          value={value ? moment(value) : null}
          onChange={onChange}
          slots={{ textField }}
          disabled={disabled}
          disablePast={disablePast}
          disableFuture={disableFuture}
          shouldDisableDate={shouldDisableDate}
          localeText={{
            toolbarTitle: '',
          }}
        />
        <Box ml="1.5rem" />
        {showTimePicker && (
          <StyledTimePicker
            onChange={inputProps?.onChange}
            value={disabledTime ? null : value}
            disabled={isTimePickerDisabled}
            allowEmpty={false}
            showSecond={showSecond}
            disabledHours={() => getDisabledHours(fieldName)}
            disabledMinutes={(selectedHour) =>
              getDisabledMinutes(selectedHour, fieldName)
            }
            format={format}
            showNow={false}
            inputIcon={
              <InputAdornment
                position="end"
                style={{ color: isTimePickerDisabled ? '#00000061' : '#666' }}
              >
                <AccessTimeIcon color="inherit" />
              </InputAdornment>
            }
            minuteStep={minuteStep}
            use12Hours={use12Hours}
          />
        )}
      </Box>
      {hasError && (
        <Box mx="1.4rem">
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
    </LocalizationProvider>
  );
};

DateTimePicker.propTypes = {
  input: PropTypes.instanceOf(Object).isRequired,
  meta: PropTypes.instanceOf(Object).isRequired,
  disabled: PropTypes.bool,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
  disabledTime: PropTypes.bool,
  showSecond: PropTypes.bool,
  use12Hours: PropTypes.bool,
  showTimePicker: PropTypes.bool,
  shouldDisableDate: PropTypes.func,
  onChangeFormatter: PropTypes.func,
  getDisabledHours: PropTypes.func,
  getDisabledMinutes: PropTypes.func,
  setReservationDate: PropTypes.func,
  minuteStep: PropTypes.number,
  format: PropTypes.string,
  style: PropTypes.instanceOf(Object),
};

export default DateTimePicker;
